import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Upload, GetApp, Help } from '@mui/icons-material';
import ImportResult from './ImportResult';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationSettings } from '../hooks/useTransactions';
import type { AccountType, TransactionType } from '../types';
import { FileParser, FilePreviewUtil, type ColumnMapping, type FormatDetectionResult } from '../services/fileParser';
import { TransactionIngestionService } from '../services/transactionIngestion';
import { getAllCSVSamples, downloadCSVSample } from '../utils/csvSample';

interface ParsedRow {
  date: string;
  amount: number;
  category?: string;
  type: TransactionType;
  error?: string;
  rowNumber: number;
}

interface FileImportResult {
  success: ParsedRow[];
  errors: ParsedRow[];
  duplicates: ParsedRow[];
  total: number;
}

interface FileImportProps {
  accountType: AccountType;
  onClose: () => void;
  onSuccess: (result: FileImportResult) => void;
}

const FileImport: React.FC<FileImportProps> = ({ accountType, onClose, onSuccess }) => {
  const { user, organization } = useAuth();
  const queryClient = useQueryClient();
  const { data: orgSettings, isLoading: settingsLoading } = useOrganizationSettings();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [formatDetection, setFormatDetection] = useState<FormatDetectionResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<'select' | 'preview' | 'mapping' | 'processing' | 'result'>('select');
  const [importResult, setImportResult] = useState<FileImportResult | null>(null);

  // ファイル選択処理
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      parseFilePreview(file);
    }
  }, []);

  // ドラッグ&ドロップ処理
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      parseFilePreview(file);
    }
  }, []);

  // ファイルプレビュー解析
  const parseFilePreview = async (file: File) => {
    setCurrentStep('preview');
    setUploadProgress(25);

    try {
      // 新しいFilePreviewUtilを使用
      const previewRows = await FilePreviewUtil.getPreviewData(file, 10);
      setPreviewData(previewRows);
      setUploadProgress(40);
      
      // フォーマット自動検出を実行
      const detectionResult = FileParser.detectFormat(previewRows);
      setFormatDetection(detectionResult);
      
      // 自動検出された列マッピングを設定
      setColumnMapping(detectionResult.suggestedMapping);
      
      setUploadProgress(50);
      setCurrentStep('mapping');
    } catch (error) {
      console.error('ファイル解析エラー:', error);
      alert(`ファイルの解析に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
      setCurrentStep('select');
      setUploadProgress(0);
    }
  };

  // 列マッピング設定
  const availableColumns = ['日付', '金額', '収支', 'カテゴリ', '摘要', 'メモ', '無視'];
  
  const handleColumnMappingChange = (columnIndex: number, mappingValue: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [columnIndex]: mappingValue as any
    }));
  };

  // データ処理・インポート実行
  const handleImport = async () => {
    if (!user || !organization || !selectedFile) return;

    // 組織設定の読み込み待ち
    if (settingsLoading) {
      return;
    }

    setIsProcessing(true);
    setCurrentStep('processing');
    setUploadProgress(75);

    try {
      // 新しいFileParserを使用
      const fiscalYearStartMonth = orgSettings?.fiscalYearStartMonth || 4;
      
      const parseResult = await FileParser.parseFile(selectedFile, {
        accountType,
        columnMapping,
        fileName: selectedFile.name,
        hasHeader: true, // デフォルトでヘッダーありと仮定
        fiscalYearStartMonth // 会計年度開始月
      });

      setUploadProgress(85);

      // TransactionIngestionServiceを使用してデータベースに保存
      const ingestionService = new TransactionIngestionService(organization.id, user.id);
      const ingestionResult = await ingestionService.ingestTransactions(parseResult.normalizedTransactions);

      // 結果をFileImportResultに変換
      const fileImportResult: FileImportResult = {
        success: ingestionResult.success.map(t => ({
          date: t.transaction_date,
          amount: t.amount,
          type: t.type,
          category: t.category,
          rowNumber: t.source_metadata?.row_number || 0
        })),
        errors: [
          // パース時のエラー
          ...parseResult.errors.map(e => ({
            date: e.date || '',
            amount: e.amount || 0,
            type: e.type || 'income',
            rowNumber: e.rowNumber,
            error: e.error
          })),
          // 取り込み時のエラー
          ...ingestionResult.errors.map(e => ({
            date: e.transaction_date,
            amount: e.amount,
            type: e.type,
            rowNumber: e.source_metadata?.row_number || 0,
            error: e.error
          }))
        ],
        duplicates: ingestionResult.duplicates.map(t => ({
          date: t.transaction_date,
          amount: t.amount,
          type: t.type,
          category: t.category,
          rowNumber: t.source_metadata?.row_number || 0
        })),
        total: parseResult.totalRows
      };
      
      // インポート履歴を記録
      await recordImportHistory(fileImportResult);
      
      setImportResult(fileImportResult);
      setUploadProgress(100);
      setCurrentStep('result');
      
      // キャッシュ無効化
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-data'] });
      queryClient.invalidateQueries({ queryKey: ['yearly-data'] });
      queryClient.invalidateQueries({ queryKey: ['account-balances'] });
      
      onSuccess(fileImportResult);
    } catch (error) {
      console.error('インポートエラー:', error);
      alert(`インポートに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 古い関数は削除済み - 新しいモジュール化されたサービスを使用

  // インポート履歴記録
  const recordImportHistory = async (result: FileImportResult) => {
    if (!user || !organization || !selectedFile) return;

    try {
      await supabase.from('import_histories').insert({
        organization_id: organization!.id,
        file_name: selectedFile.name,
        account_type: accountType,
        status: result.errors.length > 0 ? 'warning' : 'success',
        record_count: result.success.length,
        error_details: result.errors.length > 0 ? { errors: result.errors } : null,
        imported_by: user!.id
      });
    } catch (error) {
      console.error('履歴記録エラー:', error);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        ファイルインポート - {accountType === 'general' ? '一般会計' : '積立会計'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: 400 }}>
          {/* プログレスバー */}
          {currentStep !== 'select' && (
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ mb: 3 }}
            />
          )}

          {/* ファイル選択 */}
          {currentStep === 'select' && (
            <>
              <Box
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                <Upload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  ファイルをドラッグ&ドロップまたはクリックして選択
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  対応形式: Excel (.xlsx, .xls), CSV (.csv)
                </Typography>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button variant="contained" component="span">
                    ファイルを選択
                  </Button>
                </label>
              </Box>

              {/* CSVサンプルフォーマット */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Help sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">
                    CSVサンプルフォーマット
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  以下のサンプルファイルをダウンロードして、データの準備にご利用ください
                </Typography>
                
                <List>
                  {getAllCSVSamples().map((sample, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={sample.description}
                        secondary={`ファイル名: ${sample.filename}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="サンプルファイルをダウンロード">
                          <IconButton
                            edge="end"
                            onClick={() => downloadCSVSample(sample)}
                            color="primary"
                          >
                            <GetApp />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {/* プレビュー・列マッピング */}
          {(currentStep === 'preview' || currentStep === 'mapping') && previewData.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                データプレビュー・列マッピング設定
              </Typography>
              
              {/* フォーマット検出結果の表示 */}
              {formatDetection && (
                <Box sx={{ mb: 2 }}>
                  <Alert 
                    severity={formatDetection.confidence >= 0.7 ? 'success' : formatDetection.confidence >= 0.4 ? 'warning' : 'info'}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2">
                      <strong>自動検出結果:</strong> {formatDetection.detectedPattern === 'standard_format' ? '標準フォーマット' : 
                                                     formatDetection.detectedPattern === 'standard_ledger' ? '標準的な収支表' : 
                                                     formatDetection.detectedPattern === 'income_expense_split' ? '収入支出分離形式' : 
                                                     '不明なフォーマット'}
                      &nbsp;(信頼度: {Math.round(formatDetection.confidence * 100)}%)
                    </Typography>
                  </Alert>
                  {formatDetection.confidence >= 0.7 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      列マッピングを自動設定しました。必要に応じて調整してください。
                    </Typography>
                  )}
                </Box>
              )}
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {previewData[0]?.map((header: any, index: number) => (
                        <TableCell key={index}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {String(header)}
                            </Typography>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={columnMapping[index] || '無視'}
                                onChange={(e) => handleColumnMappingChange(index, e.target.value)}
                              >
                                {availableColumns.map(col => (
                                  <MenuItem key={col} value={col}>{col}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.slice(1, 6).map((row: any[], rowIndex: number) => (
                      <TableRow key={rowIndex}>
                        {row.map((cell: any, cellIndex: number) => (
                          <TableCell key={cellIndex}>
                            {String(cell)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* 処理中 */}
          {currentStep === 'processing' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                データを処理中...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ファイルを解析してデータベースに保存しています
              </Typography>
            </Box>
          )}

          {/* 結果表示 */}
          {currentStep === 'result' && importResult && (
            <ImportResult result={importResult} accountType={accountType} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {currentStep === 'result' ? (
          <Button onClick={onClose} variant="contained">閉じる</Button>
        ) : (
          <>
            <Button onClick={onClose}>キャンセル</Button>
            {currentStep === 'mapping' && (
              <Button 
                onClick={handleImport}
                variant="contained"
                disabled={Object.keys(columnMapping).length === 0 || isProcessing || settingsLoading}
              >
                {settingsLoading ? '設定読み込み中...' : 'インポート実行'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileImport;